// Utility to persist a pending search intent from the Home hero so it can be
// applied after sign-in / sign-up. Kept intentionally tiny + framework-free.

const KEY = "bera_stays_pending_search";
const TTL_MS = 24 * 60 * 60 * 1000; // 24h

export interface PendingSearch {
  path: string; // e.g. "/explorar"
  query: string; // starts with "?" or empty
  savedAt: number;
}

export function savePendingSearch(path: string, query: string) {
  try {
    const q = query.startsWith("?") || query === "" ? query : `?${query}`;
    const data: PendingSearch = { path, query: q, savedAt: Date.now() };
    localStorage.setItem(KEY, JSON.stringify(data));
  } catch {}
}

export function readPendingSearch(): PendingSearch | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as PendingSearch;
    if (!data?.path || Date.now() - (data.savedAt || 0) > TTL_MS) {
      localStorage.removeItem(KEY);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

export function clearPendingSearch() {
  try {
    localStorage.removeItem(KEY);
  } catch {}
}

/**
 * Resolve where to send the user after sign-in / sign-up.
 * Priority: explicit ?redirect= param (same-origin) > pending search > fallback.
 * Always returns an internal path starting with "/".
 */
export function resolvePostAuthRedirect(fallback: string): string {
  try {
    const params = new URLSearchParams(window.location.search);
    const redirect = params.get("redirect");
    if (redirect && redirect.startsWith("/") && !redirect.startsWith("//")) {
      return redirect;
    }
  } catch {}
  const pending = readPendingSearch();
  if (pending) {
    clearPendingSearch();
    return `${pending.path}${pending.query || ""}`;
  }
  return fallback;
}
